import { type FC, useEffect } from 'react';

import { Plus } from 'lucide-react';
import { type UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import { useGetAssetsStatusesQuery } from '@features/asset-management/api/asset-management.api';
import { AssetStatus } from '@features/asset-management/asset-management.types';
import { TokenLimitAlert } from '@features/investigations/components/modals/add-new-investigation/TokenLimitAlert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@shared/components/ui';
import { GenericButton, GenericDateRangePicker, GenericMultiSelect } from '@shared/components/ui/custom';

import { useAddNewTrace } from './hooks/useAddNewTrace';
import type { NewInvestigationFormValues } from './investigation.schema';
import { TARGET_TYPES } from './investigation.schema';
import { getAssetOptions } from './investigation.utils';
import { TargetRow } from './TargetRow';

export interface NewInvestigationFormProps {
  form: UseFormReturn<NewInvestigationFormValues>;
  onClose: () => void;
  onSuccess: () => void;
  hasReachedLimit: boolean;
  isEstimating: boolean;
  estimationHasError: boolean;
  markAsLaunched: () => void;
  resetAllMutations: () => void;
}

export const NewInvestigationForm: FC<NewInvestigationFormProps> = ({
  form,
  onClose,
  onSuccess,
  hasReachedLimit,
  isEstimating,
  estimationHasError,
  markAsLaunched,
  resetAllMutations,
}) => {
  const { data: assetsData = [], isLoading: isAssetsLoading } = useGetAssetsStatusesQuery();
  const assetOptions = getAssetOptions(assetsData ?? []);
  const readyAssets = (assetsData ?? []).filter((asset) => asset.status === AssetStatus.READY);
  const hasReadyAssetsInResponse = readyAssets.length > 0;
  const defaultAssetNames = readyAssets.map((a) => a.name);

  const { submitNewTrace, isLoading } = useAddNewTrace();

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'targets',
  });

  const watchedTargets = useWatch({ control: form.control, name: 'targets' });

  const usedTypes = new Set(watchedTargets?.map((t) => t.type).filter(Boolean));

  useEffect(() => {
    if (isAssetsLoading) return;

    if (!hasReadyAssetsInResponse) {
      form.setError('assets', {
        type: 'manual',
        message: 'An investigation requires at least one READY application instance to proceed.',
      });
      return;
    }

    if (form.getValues('assets')?.length === 0) {
      form.setValue('assets', defaultAssetNames, { shouldDirty: false, shouldValidate: false });
    }

    form.clearErrors('assets');
  }, [isAssetsLoading, hasReadyAssetsInResponse, form, defaultAssetNames]);

  const handleAddCondition = () => {
    const available = TARGET_TYPES.find((t) => !usedTypes.has(t));
    if (available) {
      append({ type: available, value: '' });
    }
  };

  const handleOnClose = () => {
    onClose();
    resetAllMutations();
  };

  const handleValidSubmit = async (data: NewInvestigationFormValues) => {
    try {
      await submitNewTrace({ ...data, sendEmailNotification: true }, form.setError);
      markAsLaunched();
      handleOnClose();
      onSuccess();
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  return (
    <Form {...form} data-testid="add-investigation-form-root">
      <form data-testid="add-investigation-form">
        <div
          className="flex-1 overflow-y-auto scroll-container bg-background p-8 max-h-[80vh]"
          data-testid="add-investigation-modal-scroll-area"
        >
          <div className="w-full max-w-[640px] mx-auto space-y-6 gap-1" data-testid="add-investigation-form-fields">
            <FormField
              control={form.control}
              name="traceName"
              render={({ field }) => (
                <FormItem className="w-full" data-testid="add-investigation-formitem-name">
                  <FormLabel
                    className="flex items-center gap-2"
                    data-testid="add-investigation-label-name"
                    htmlFor="trace-name"
                  >
                    Name
                  </FormLabel>
                  <FormControl data-testid="add-investigation-control-name">
                    <Input
                      placeholder="Enter a unique name"
                      {...field}
                      data-testid="add-investigation-input-name"
                      id="trace-name"
                    />
                  </FormControl>
                  <FormMessage data-testid="add-investigation-message-name" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dateRange"
              render={({ field }) => (
                <FormItem className="w-full" data-testid="add-investigation-formitem-daterange">
                  <FormLabel className="flex items-center gap-2" data-testid="add-investigation-label-daterange">
                    Time Range
                  </FormLabel>
                  <FormControl data-testid="add-investigation-control-daterange">
                    <GenericDateRangePicker
                      value={field.value}
                      onChange={field.onChange}
                      className="w-full"
                      data-testid="add-investigation-picker-daterange"
                    />
                  </FormControl>
                  <FormMessage data-testid="add-investigation-message-daterange" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="assets"
              render={({ field }) => (
                <FormItem className="w-full" data-testid="add-investigation-formitem-application-instances">
                  <FormLabel
                    className="flex items-center gap-2"
                    data-testid="add-investigation-label-application-instances"
                  >
                    Application Instances
                  </FormLabel>
                  <FormControl data-testid="add-investigation-control-application-instances">
                    <GenericMultiSelect
                      options={assetOptions}
                      value={field.value ?? []}
                      onChange={field.onChange}
                      placeholder={
                        isAssetsLoading ? 'Loading application instances...' : 'Select application instances'
                      }
                      searchPlaceholder="Search application instances..."
                      className="w-full"
                      data-testid="add-investigation-multiselect-application-instances"
                    />
                  </FormControl>
                  <FormMessage data-testid="add-investigation-message-application-instances" />
                </FormItem>
              )}
            />

            <div className="space-y-3" data-testid="add-investigation-scope-section">
              <span
                id="add-investigation-scope-label"
                className="text-sm font-medium leading-none"
                data-testid="add-investigation-label-scope"
              >
                Scope
              </span>
              <fieldset
                className="border border-border rounded-lg p-4 space-y-4 pb-10"
                aria-labelledby="add-investigation-scope-label"
                data-testid="add-investigation-scope-list"
              >
                {fields.map((field, index) => (
                  <TargetRow
                    key={field.id}
                    form={form}
                    index={index}
                    onRemove={remove}
                    usedTypes={usedTypes}
                    isLast={index === fields.length - 1}
                  />
                ))}

                {usedTypes.size < TARGET_TYPES.length && (
                  <div className="pt-2" data-testid="add-investigation-scope-add-btn-wrapper">
                    <GenericButton
                      type="button"
                      variant="ghost"
                      onClick={handleAddCondition}
                      className="text-primary hover:bg-primary/10 pl-0 h-auto font-normal"
                      label="New condition"
                      icon={Plus}
                      iconPosition="left"
                      data-testid="add-investigation-scope-add-btn"
                    />
                  </div>
                )}
              </fieldset>
            </div>

            <TokenLimitAlert hasReachedLimit={hasReachedLimit} />

            <div className="flex items-center gap-3" data-testid="add-investigation-actions">
              <GenericButton
                variant="secondary"
                data-testid="add-investigation-cancel-btn"
                onClick={handleOnClose}
                type="button"
                label="Cancel"
              />
              <GenericButton
                label="Save & Launch"
                type="submit"
                isLoading={isLoading}
                onClick={form.handleSubmit(handleValidSubmit)}
                data-testid="add-investigation-save-btn"
                disabled={
                  isEstimating ||
                  estimationHasError ||
                  hasReachedLimit ||
                  form.formState.isSubmitting ||
                  !!form.formState.errors.assets ||
                  (!form.formState.isValid && form.formState.isSubmitted)
                }
              />
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
